/* eslint-disable react/prop-types */
import {memo, useContext} from 'react'

import Context from '@s-ui/react-context'
import GalleryBasic, {COUNTER_POSITIONS, SLIDER_IMAGE_SIZES} from '@adv-mt/mt-gallery-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconCarCertified from '@adv-mt/mt-svg-icons/lib/CarCertified'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {BADGES_TYPES} from './settings.js'

const _addVideo = ({ad, mediaItems, manualPlay, timeOffset}) => {
  if (ad.videos === undefined || ad.videos.length === 0) {
    return {items: mediaItems, selectedItem: undefined}
  }

  const video = {
    isVideo: true,
    ...ad.videos[0],
    manualPlay,
    timeOffset,
    title: `Vídeo ${ad.title}`
  }

  const mediaItemsWithVideo = [
    ...(video.position === 'FIRST' ? [video] : []),
    ...mediaItems,
    ...(video.position === 'LAST' ? [video] : [])
  ]

  const selectedItem = manualPlay && video.position === 'LAST' ? mediaItemsWithVideo.length - 1 : undefined

  return {items: mediaItemsWithVideo, selectedItem}
}

const parseImagesForGallery = ({ad, images = [], manualPlay, timeOffset}) => {
  const mediaItems = images.map(img => ({src: img, alt: ad.title, title: ad.title}))
  const {items, selectedItem} = _addVideo({
    ad,
    mediaItems,
    manualPlay,
    timeOffset
  })
  return {items, selectedItem}
}

const GalleryWrapper = ({ad, adType, videoPlay, videoTime}) => {
  const {track} = useAnalyticsTrack()
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const initialMediaQueryMatch = {
    defaultMatches: !isMobile
  }

  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm, initialMediaQueryMatch)
  const isHigherThanTabletResolution = useMediaQuery(useMediaQueryBreakpoints.lg, initialMediaQueryMatch)

  const {TAGS_VISIBILITY, OFFER_TYPE_NEW, OFFER_TYPE_SUBSCRIPTION} = config.get('search')

  const offerTypes = config.get('search').offerTypes

  const {VEHICLE_STATE_IDS} = config.get('subscription')

  const {hasStock, isCertified, isFinanced, isUrgent, subscriptionVehicleState} = ad
  const {offerTypeId} = adType

  const _getOfferTypeTag = () => {
    const hasOfferType = Boolean(offerTypeId)
    if (!hasOfferType) return

    const isTag = offerTypes.some(({id}) => offerTypeId === id)
    return isTag ? offerTypes[offerTypeId].label : undefined
  }

  const {items: images, selectedItem} = parseImagesForGallery({
    ad,
    images: ad.photos,
    manualPlay: videoPlay,
    timeOffset: videoTime
  })
  const showThumbnails = isHigherThanMobileResolution && images.length > 1

  const offerTypeTag = _getOfferTypeTag()

  const isNewVehicle = offerTypeId === OFFER_TYPE_NEW

  const isSubscriptionAd = offerTypeId === OFFER_TYPE_SUBSCRIPTION

  const isNewVehicleSubscription = subscriptionVehicleState === VEHICLE_STATE_IDS.NEW

  const isUsedVehicleSubscription = subscriptionVehicleState === VEHICLE_STATE_IDS.USED

  const showStockTag = hasStock && (isSubscriptionAd || isNewVehicle)

  const badges = [
    isCertified &&
      !isNewVehicle && {
        icon: IconCarCertified,
        label: i18n.t('COMMON_CERTIFIED'),
        type: BADGES_TYPES.IS_CERTIFIED
      },
    TAGS_VISIBILITY.URGENT &&
      isUrgent && {
        label: i18n.t('COMMON.URGENT'),
        type: BADGES_TYPES.IS_URGENT,
        design: 'soft'
      },
    TAGS_VISIBILITY.FINANCED &&
      isFinanced && {
        label: i18n.t('COMMON_FINANCED'),
        type: BADGES_TYPES.IS_FINANCED,
        design: 'soft'
      },
    showStockTag && {
      label: i18n.t('COMMON.HAS_STOCK'),
      type: BADGES_TYPES.HAS_STOCK,
      design: 'soft'
    },
    offerTypeTag && {
      label: i18n.t(offerTypeTag),
      type: BADGES_TYPES.INFO,
      design: 'soft'
    },
    isNewVehicleSubscription && {
      label: i18n.t('COMMON.OFFER_TYPE.NEW'),
      type: BADGES_TYPES.INFO,
      design: 'soft'
    },
    isUsedVehicleSubscription && {
      label: i18n.t('COMMON.PRE_OWNED'),
      type: BADGES_TYPES.INFO,
      design: 'soft'
    }
  ].filter(Boolean)

  const counterPosition = COUNTER_POSITIONS.bottomLeft

  const onVideoButtonClick = async () => {
    const {
      TRACKING_EVENT_NAMES: {AD_VIDEO_BUTTON_CLICKED}
    } = config.get('tracking')

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      event: AD_VIDEO_BUTTON_CLICKED,
      origin: 'detail'
    })

    track(eventData)
  }

  const onVideoPlay = async () => {
    const {
      TRACKING_EVENT_NAMES: {AD_VIDEO_PLAYED}
    } = config.get('tracking')

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      event: AD_VIDEO_PLAYED,
      origin: 'detail'
    })

    track(eventData)
  }

  return (
    <GalleryBasic
      counterPosition={counterPosition}
      badges={badges}
      displayVideoControls={true}
      enableCounter
      hideCounterWhenPlayingVideo={true}
      images={images}
      onVideoButtonClick={onVideoButtonClick}
      onVideoPlay={onVideoPlay}
      selectedImage={selectedItem}
      showFullSizeBtn={!isHigherThanTabletResolution}
      showLargeVideoButton
      showThumbnails={showThumbnails}
      sliderImageSize={SLIDER_IMAGE_SIZES.LARGE}
    />
  )
}

export default memo(GalleryWrapper)
